import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Status from 'components/field/Status';
import Card from 'components/global/Card';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import ListProfileImage from 'components/global/ListProfileImage';
import Loading from 'components/global/Loading';
import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';
import PhoneViewer from 'components/list/HelperComponents/PhoneViewer';
import Maps from 'components/maps/Maps';

import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';

import { openAxios } from 'services/axios';
import { OPEN, STORAGE_PUBLIC } from 'services/consts';

import { languages } from 'utils/localization';

import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OTOFlexTracking.styled';

const OTOFlexTracking = () => {
	const [languageMenuButtonRef, setLanguageMenuButtonRef] = useState(null);
	const changeLanguage = useChangeLanguage();
	const maps = window?.google?.maps;
	const [map, setMap] = useState(null);
	const [data, setData] = useState(null);
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	const localization = useSelector((state) => state.layout.localization);
	const [searchParams] = useSearchParams();
	const enc = searchParams.get('enc');
	const markers = useRef([]);
	const fetchTimer = useRef(null);
	const { getLocaleDateWithTime } = useLocale();

	const fetchData = async () => {
		const response = await openAxios.post(OPEN.GET_OTO_FLEX_TRACKING_INFO, { enc });
		setData(response.data);
		setLoading(false);
	};

	const fillMap = async () => {
		while (markers.length) {
			markers.pop().setMap(null);
		}
		const bounds = new maps.LatLngBounds();
		const { AdvancedMarkerElement } = await maps.importLibrary('marker');

		if (parseFloat(data?.pickupLat) && parseFloat(data?.pickupLon)) {
			bounds.extend({ lat: parseFloat(data?.pickupLat), lng: parseFloat(data?.pickupLon) });
			const markerImage = document.createElement('img');
			markerImage.src = `${STORAGE_PUBLIC}/tracking-system/icons/branch.png`;

			const marker = new AdvancedMarkerElement({
				position: { lat: parseFloat(data?.pickupLat), lng: parseFloat(data?.pickupLon) },
				content: markerImage,
				title: t('mapview.flex.startPoint'),
				map
			});

			marker.content.classList.add('bounce');
			markers.current.push(marker);
		}
		if (parseFloat(data?.deliveryLat) && parseFloat(data?.deliveryLon)) {
			bounds.extend({ lat: parseFloat(data?.deliveryLat), lng: parseFloat(data?.deliveryLon) });
			const markerImage = document.createElement('img');

			markerImage.src = `${STORAGE_PUBLIC}/tracking-system/icons/delivery.png`;

			const marker = new AdvancedMarkerElement({
				position: { lat: parseFloat(data?.deliveryLat), lng: parseFloat(data?.deliveryLon) },
				content: markerImage,
				title: t('mapview.flex.finishPoint'),
				map
			});
			marker.content.classList.add('bounce');
			markers.current.push(marker);
		}
		if (parseFloat(data?.driverLat) && parseFloat(data?.driverLon)) {
			bounds.extend({ lat: parseFloat(data?.driverLat), lng: parseFloat(data?.driverLon) });

			const markerImage = document.createElement('img');

			markerImage.src = `${STORAGE_PUBLIC}/tracking-system/icons/driver.png`;

			const marker = new AdvancedMarkerElement({
				position: { lat: parseFloat(data?.driverLat), lng: parseFloat(data?.driverLon) },
				content: markerImage,
				title: t('mapview.flex.finishPoint'),
				map
			});
			marker.content.classList.add('bounce');
			markers.current.push(marker);
		}
		map.fitBounds(bounds);
		map.setCenter(bounds.getCenter());
	};

	useEffect(() => {
		if (enc) {
			fetchData();
			const interval = setInterval(() => {
				fetchData();
			}, 30000);
			fetchTimer.current = interval;
		}

		const listener = () => {
			if (document.visibilityState === 'visible') {
				const interval = setInterval(() => {
					fetchData();
				}, 30000);
				fetchTimer.current = interval;
			} else {
				clearInterval(fetchTimer.current);
			}
		};
		document.addEventListener('visibilitychange', listener);

		return () => document.removeEventListener('visibilitychange', listener);
	}, []);

	useEffect(() => {
		if (map && maps && data) {
			maps.event.addListenerOnce(map, 'idle', () => fillMap());
		}
	}, [map, data]);

	if (loading) return <Loading />;

	return (
		<S.OuterContainer>
			<S.Container>
				<S.Header>
					<S.Logo
						width={'10rem'}
						height={'10rem'}
						alt="logo"
						url={'https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png'}
						onClick={() => (window.location.href = 'https://www.tryoto.com')}
					/>
					<div>
						<Button variant="borderless" ref={setLanguageMenuButtonRef}>
							<Image width="20px" height="15px" alt="lang_icon" url={localization.icon} />
							<Typography>{localization.name}</Typography>
							<Icon icon={faAngleDown} />
						</Button>
						{languageMenuButtonRef && (
							<Menu
								buttonRef={languageMenuButtonRef}
								menuItems={languages.map((language) => ({
									label: language.name,
									img: language.icon,
									imgProps: { width: '20px', height: '15px' },
									selected: language.code === localization.code,
									action: async () => {
										await changeLanguage(language.code);
									}
								}))}
							/>
						)}
					</div>
				</S.Header>
				<S.MiddleContainer>
					<S.LeftContainer>
						<Card rounded>
							<Typography variant="p2" fontWeight={500}>
								orderNumber
							</Typography>
							<S.Divider />
							<Typography variant="p2" fontWeight={500}>
								{data.orderCode}
							</Typography>
						</Card>
						<Card rounded>
							<Typography variant="p2" fontWeight={500}>
								status
							</Typography>
							<S.Divider />
							<Status value={data.status} style={{ marginBottom: '1rem' }} />
							<Typography color="var(--content-2)">{getLocaleDateWithTime(data.date)}</Typography>
						</Card>
						<Card rounded>
							<Typography variant="p2" fontWeight={500}>
								tripDetails
							</Typography>
							<S.Divider />
							<S.TripItem>
								<S.ColorDot color="var(--green)" />
								<div>
									<Typography variant="p2" color="var(--content-2)" fontWeight={500}>
										otoFlexTrackingPickup
									</Typography>
									<Typography variant="p2" noWrap fontWeight={500}>
										{data.pickupAddress}
									</Typography>
								</div>
							</S.TripItem>
							<S.Divider />
							<S.TripItem>
								<S.ColorDot color="var(--red)" />
								<div>
									<Typography variant="p2" color="var(--content-2)" fontWeight={500}>
										otoFlexTrackingDropoff
									</Typography>
									<Typography variant="p2" noWrap fontWeight={500}>
										{data.deliveryAddress}
									</Typography>
								</div>
							</S.TripItem>
						</Card>
						<Card rounded>
							<Typography variant="p2" fontWeight={500}>
								otoFlexTrackingCourierInformation
							</Typography>
							<S.Divider />
							<div style={{ display: 'flex' }}>
								<ListProfileImage
									data={{
										fullName: data.driverName ?? '-',
										avatarPath: data.avatarPath,
										activeDisabled: true
									}}
								/>
								{data.driverPhone && <PhoneViewer value={data.driverPhone} />}
							</div>
						</Card>
					</S.LeftContainer>
					<S.RightContainer>
						<Maps setMap={setMap} />
					</S.RightContainer>
				</S.MiddleContainer>

				<S.Footer>
					<Typography>OTO Global Inc. © 2025</Typography>
					<Button
						variant="borderless"
						onClick={() => {
							window.location.href = 'mailto:support@tryoto.com';
						}}>
						<Icon icon={faEnvelope}></Icon>
						<Typography>support@tryoto.com</Typography>
					</Button>
				</S.Footer>
			</S.Container>
		</S.OuterContainer>
	);
};

export default OTOFlexTracking;
